/* Snap theme — storefront behaviour. No dependencies. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const Snap = window.Snap || { routes: {}, strings: {} };
  const strings = Snap.strings || {};
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------ */
  /* Cart API                                                            */
  /* ------------------------------------------------------------------ */

  const rootUrl = () => {
    const root = (Snap.routes && Snap.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const readJson = async (res) => {
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      /* non-JSON error page */
    }
    if (!res.ok || (data && data.status && data.status !== 200)) {
      const message = (data && (data.description || data.message)) || strings.cartError || 'Error';
      throw new Error(message);
    }
    return data;
  };

  const Cart = {
    async sections(ids = ['cart-drawer']) {
      const res = await fetch(`${rootUrl()}?sections=${ids.join(',')}`, { headers: { Accept: 'application/json' } });
      return res.ok ? res.json() : null;
    },
    async add(formData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(Snap.routes.cartAdd || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      return readJson(res);
    },
    async change(line, quantity) {
      const res = await fetch(Snap.routes.cartChange || '/cart/change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
      });
      return readJson(res);
    },
    async note(note) {
      await fetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    },
  };

  const setCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  /* ------------------------------------------------------------------ */
  /* Dialogs: size chart, menu, search                                   */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.modalOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
        document.documentElement.classList.add('scroll-lock');
      }
      return;
    }
    if (event.target.closest('[data-modal-close]')) {
      const dialog = event.target.closest('dialog');
      if (dialog) dialog.close();
      return;
    }
    // A click that lands on the <dialog> itself is a click on its backdrop.
    if (event.target instanceof HTMLDialogElement && event.target.open) event.target.close();
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement && !$('dialog[open]')) {
        document.documentElement.classList.remove('scroll-lock');
      }
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Cart drawer                                                         */
  /* ------------------------------------------------------------------ */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('#CartDrawer', this);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const button = event.target.closest('[data-line-change]');
        if (button) {
          event.preventDefault();
          this.updateLine(Number(button.dataset.line), Number(button.dataset.quantity));
        }
      });

      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-qty]');
        if (input) this.updateLine(Number(input.dataset.line), Math.max(0, parseInt(input.value, 10) || 0));
        if (event.target.matches('[data-cart-note]')) Cart.note(event.target.value);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open]');
        if (opener && Snap.cartType === 'drawer') {
          event.preventDefault();
          this.open(opener);
        }
      });

      // Apps that add to the cart themselves (bundle widgets, upsells) broadcast
      // these events; many fire several at once, so coalesce into one refresh.
      let timer;
      const refreshSoon = () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.refresh(), 150);
      };
      ['cart:refresh', 'cart:updated', 'cart-drawer:refresh', 'theme:cart:change'].forEach((name) =>
        document.addEventListener(name, refreshSoon)
      );
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('scroll-lock');
      setTimeout(() => {
        const close = $('[data-cart-close]', this.panel);
        if (close) close.focus();
      }, 50);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('scroll-lock');
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    trapFocus(event) {
      const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), textarea, select', this.panel).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async refresh() {
      try {
        const sections = await Cart.sections(['cart-drawer']);
        if (sections) this.render(sections['cart-drawer']);
      } catch (e) {
        /* keep the current drawer */
      }
    }

    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.getElementById('CartDrawer');
      const current = $('#CartDrawer', this);
      if (next && current) current.innerHTML = next.innerHTML;
      const counter = doc.querySelector('[data-cart-item-count]');
      if (counter) setCartCount(Number(counter.dataset.cartItemCount));
    }

    async updateLine(line, quantity) {
      this.classList.add('is-loading');
      const error = $('[data-cart-error]', this);
      if (error) error.hidden = true;
      try {
        const data = await Cart.change(line, quantity);
        this.render(data.sections && data.sections['cart-drawer']);
        document.dispatchEvent(new CustomEvent('snap:cart:updated', { detail: data }));
      } catch (err) {
        const box = $('[data-cart-error]', this);
        if (box) {
          box.textContent = err.message;
          box.hidden = false;
        }
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  define('cart-drawer', CartDrawer);

  /* ------------------------------------------------------------------ */
  /* Product data + variant changes                                      */
  /* ------------------------------------------------------------------ */

  const productCache = new Map();
  const productData = (sectionId) => {
    if (!productCache.has(sectionId)) {
      const el = document.getElementById(`ProductJSON-${sectionId}`);
      let data = { variants: [], saveTemplate: '' };
      if (el) {
        try {
          data = JSON.parse(el.textContent);
        } catch (e) {
          /* malformed JSON: picker stays inert */
        }
      }
      productCache.set(sectionId, data);
    }
    return productCache.get(sectionId);
  };

  const applyVariant = (sectionId, variant, options) => {
    const section = document.getElementById(`MainProduct-${sectionId}`);
    if (!section) return;
    const data = productData(sectionId);

    $$('[data-variant-id]', section).forEach((input) => {
      if (!variant || input.value === String(variant.id)) return;
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const price = $('[data-price-current]', section);
    if (price && variant) {
      price.textContent = variant.price;
      price.classList.toggle('is-sale', !!variant.compare);
    }
    const compare = $('[data-price-compare]', section);
    if (compare) {
      compare.hidden = !(variant && variant.compare);
      if (variant && variant.compare) compare.textContent = variant.compare;
    }
    const badge = $('[data-price-badge]', section);
    if (badge) {
      badge.hidden = !(variant && variant.pct > 0);
      if (variant && variant.pct > 0) badge.textContent = data.saveTemplate.replace('[percent]', variant.pct);
    }

    const button = $('[data-add-button]', section);
    const label = $('[data-add-label]', section);
    if (button) {
      button.disabled = !(variant && variant.available);
      if (label) {
        if (!variant) label.textContent = strings.unavailable;
        else label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }
    }

    const stock = $('[data-stock-alert]', section);
    if (stock) {
      const threshold = Number(stock.dataset.threshold);
      const inventory = variant ? variant.inventory : null;
      const show = inventory !== null && inventory > 0 && inventory <= threshold;
      stock.hidden = !show;
      const count = $('[data-stock-count]', stock);
      if (count && show) count.textContent = inventory;
    }

    const stickyPrice = $('[data-sticky-price]', section);
    if (stickyPrice && variant) stickyPrice.textContent = variant.price;

    $$('[data-rail-option]', section).forEach((item) => {
      const active = options && options[Number(item.dataset.railOption)] === item.dataset.value;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (variant && variant.media) {
      const gallery = $('product-gallery', section);
      if (gallery && gallery.goToMedia) gallery.goToMedia(variant.media);
    }

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    document.dispatchEvent(new CustomEvent('snap:variant:change', { detail: { sectionId, variant } }));
  };

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', () => this.onChange());
      this.onRailSelect = (event) => {
        if (event.detail.sectionId === this.sectionId) this.selectValue(event.detail.index, event.detail.value);
      };
      document.addEventListener('snap:option:select', this.onRailSelect);
      this.updateAvailability(this.selectedOptions());
    }

    disconnectedCallback() {
      document.removeEventListener('snap:option:select', this.onRailSelect);
    }

    optionGroups() {
      return $$('[data-option-index]', this);
    }

    selectedOptions() {
      return this.optionGroups().map((group) => {
        const select = $('select', group);
        if (select) return select.value;
        const checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    selectValue(index, value) {
      const group = $(`[data-option-index="${index}"]`, this);
      if (!group) return;
      const select = $('select', group);
      if (select) {
        select.value = value;
      } else {
        const input = $$('input', group).find((el) => el.value === value);
        if (input) input.checked = true;
      }
      this.onChange();
    }

    onChange() {
      const options = this.selectedOptions();
      this.optionGroups().forEach((group, i) => {
        const label = $('[data-selected-value]', group);
        if (label) label.textContent = options[i];
      });
      const { variants } = productData(this.sectionId);
      const variant = variants.find((v) => v.options.every((value, i) => value === options[i])) || null;
      this.updateAvailability(options);
      applyVariant(this.sectionId, variant, options);
    }

    // Grey out values that have no available variant given the other selections.
    updateAvailability(options) {
      const { variants } = productData(this.sectionId);
      if (!variants.length) return;
      this.optionGroups().forEach((group, index) => {
        $$('input', group).forEach((input) => {
          const available = variants.some(
            (v) => v.available && v.options[index] === input.value && v.options.every((val, j) => j === index || val === options[j])
          );
          const label = group.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }
  }
  define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------ */
  /* Product gallery                                                     */
  /* ------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb-index]', this);
      this.thumbBar = $('.gallery__thumbs', this);
      this.sectionId = this.dataset.sectionId;
      this.index = 0;
      if (!this.track) return;

      const prev = $('[data-gallery-prev]', this);
      const next = $('[data-gallery-next]', this);
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.thumbIndex))));

      this.addEventListener('click', (event) => {
        const rail = event.target.closest('[data-rail-option]');
        if (!rail) return;
        const detail = { sectionId: this.sectionId, index: Number(rail.dataset.railOption), value: rail.dataset.value };
        document.dispatchEvent(new CustomEvent('snap:option:select', { detail }));
        // Without a variant picker on the page, still apply the choice.
        if (!document.querySelector(`variant-picker[data-section-id="${this.sectionId}"]`)) {
          const { variants } = productData(this.sectionId);
          const variant = variants.find((v) => v.options[detail.index] === detail.value);
          if (variant) applyVariant(this.sectionId, variant, variant.options);
        }
      });

      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.syncIndex());
        },
        { passive: true }
      );
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });

      const initial = this.dataset.initialMedia;
      if (initial) this.goToMedia(initial, 'auto');
    }

    syncIndex() {
      const width = this.track.clientWidth;
      if (!width) return;
      this.setActive(Math.round(this.track.scrollLeft / width));
    }

    setActive(index) {
      if (index === this.index && this.activeSet) return;
      this.activeSet = true;
      this.index = index;
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          if (this.thumbBar) {
            const left = thumb.offsetLeft - this.thumbBar.clientWidth / 2 + thumb.clientWidth / 2;
            this.thumbBar.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        $$('video', slide).forEach((video) => video.pause());
      });
    }

    go(index, behavior = 'smooth') {
      const count = this.slides.length;
      if (!count) return;
      const target = ((index % count) + count) % count;
      this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: reducedMotion ? 'auto' : behavior });
      this.setActive(target);
    }

    goToMedia(mediaId, behavior) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.go(index, behavior);
    }
  }
  define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------ */
  /* Product form                                                        */
  /* ------------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.button = $('[data-add-button]', this);
      this.error = $('[data-form-error]', this);
      // Listen on the wrapper, not the form: listeners that apps attach to the
      // form itself run first and can claim the add (preventDefault /
      // stopImmediatePropagation) before the theme adds the same product again.
      this.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      if (event.defaultPrevented) return;
      const drawer = $('cart-drawer');
      if (Snap.cartType !== 'drawer' || !drawer) return;
      event.preventDefault();
      if (this.button && this.button.disabled) return;

      this.setLoading(true);
      this.showError('');
      try {
        const data = await Cart.add(new FormData(this.form));
        drawer.render(data.sections && data.sections['cart-drawer']);
        drawer.open(this.button);
        document.dispatchEvent(new CustomEvent('snap:cart:added', { detail: data }));
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }
  define('product-form', ProductForm);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-qty]');
        if (!button || !this.input) return;
        const value = parseInt(this.input.value, 10) || 1;
        this.input.value = Math.max(1, button.dataset.qty === 'plus' ? value + 1 : value - 1);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.formId);
      const button = $('[data-sticky-button]', this);
      if (!form || !button || !('IntersectionObserver' in window)) return;
      this.hidden = false;

      const watched = form.closest('.product-block') || form;
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
        document.documentElement.classList.toggle('has-sticky-atc', scrolledPast);
      });
      this.observer.observe(watched);

      button.addEventListener('click', () => {
        const submit = $('[type="submit"]', form);
        if (this.dataset.action === 'add' && submit && !submit.disabled) {
          submit.click();
          return;
        }
        const section = this.closest('.main-product');
        const target = section && $('.product-block--app, variant-picker, .product-buy', section);
        (target || watched).scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.documentElement.classList.remove('has-sticky-atc');
    }
  }
  define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------ */
  /* Delivery estimate                                                   */
  /* ------------------------------------------------------------------ */

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 0;
      const max = Math.max(min, Number(this.dataset.max) || 0);
      const cutoff = Number(this.dataset.cutoff);
      const skipWeekends = this.dataset.skipWeekends === 'true';
      const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

      const start = new Date();
      if (start.getHours() >= cutoff) start.setDate(start.getDate() + 1);
      const addDays = (from, days) => {
        const date = new Date(from);
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          if (!skipWeekends || !isWeekend(date)) added += 1;
        }
        while (skipWeekends && isWeekend(date)) date.setDate(date.getDate() + 1);
        return date;
      };

      const earliest = addDays(start, min);
      const latest = addDays(start, max);
      let format;
      try {
        format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });
      } catch (e) {
        format = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
      }
      const dateText =
        earliest.toDateString() === latest.toDateString()
          ? format.format(earliest)
          : `${format.format(earliest)} – ${format.format(latest)}`;

      const out = $('[data-delivery-text]', this);
      if (!out) return;
      const [before, after] = (this.dataset.template || '[date]').split('[date]');
      const strong = document.createElement('strong');
      strong.textContent = dateText;
      out.replaceChildren(before || '', strong, after || '');
      this.hidden = false;
    }
  }
  define('delivery-estimate', DeliveryEstimate);

  /* ------------------------------------------------------------------ */
  /* Carousel (testimonials, video reviews)                              */
  /* ------------------------------------------------------------------ */

  class SnapCarousel extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      this.dots = $('[data-carousel-dots]', this);
      if (!this.track) return;
      const prev = $('[data-carousel-prev]', this);
      const next = $('[data-carousel-next]', this);
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.update());
        },
        { passive: true }
      );
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);
      this.build();

      const seconds = Number(this.dataset.autoplay);
      if (seconds > 0 && !reducedMotion && !designMode) this.autoplay(seconds * 1000);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      clearInterval(this.timer);
    }

    metrics() {
      const first = this.track.children[0];
      if (!first) return null;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      if (!step) return null;
      const visible = Math.max(1, Math.round((this.track.clientWidth + gap) / step));
      return { step, count: Math.max(1, this.track.children.length - visible + 1) };
    }

    build() {
      const m = this.metrics();
      if (!m || !this.dots) return;
      if (this.dots.childElementCount !== m.count) {
        this.dots.replaceChildren();
        for (let i = 0; i < m.count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('aria-label', `${i + 1} / ${m.count}`);
          dot.addEventListener('click', () => this.goTo(i));
          this.dots.append(dot);
        }
      }
      this.dots.hidden = m.count < 2;
      this.update();
    }

    current() {
      const m = this.metrics();
      return m ? Math.min(m.count - 1, Math.round(this.track.scrollLeft / m.step)) : 0;
    }

    update() {
      if (!this.dots) return;
      const index = this.current();
      $$('.carousel__dot', this.dots).forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }

    goTo(index) {
      const m = this.metrics();
      if (!m) return;
      const target = ((index % m.count) + m.count) % m.count;
      this.track.scrollTo({ left: target * m.step, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    step(delta) {
      this.goTo(this.current() + delta);
    }

    autoplay(ms) {
      const start = () => {
        clearInterval(this.timer);
        this.timer = setInterval(() => this.step(1), ms);
      };
      const stop = () => clearInterval(this.timer);
      start();
      this.addEventListener('pointerenter', stop);
      this.addEventListener('pointerleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('touchstart', stop, { passive: true });
    }
  }
  define('snap-carousel', SnapCarousel);

  /* ------------------------------------------------------------------ */
  /* Before / after slider                                               */
  /* ------------------------------------------------------------------ */

  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      const frame = $('[data-ba-frame]', this);
      const input = $('input[type="range"]', this);
      if (!frame || !input) return;
      const render = () => this.style.setProperty('--pos', `${input.value}%`);
      const fromPointer = (x) => {
        const rect = frame.getBoundingClientRect();
        input.value = Math.min(100, Math.max(0, ((x - rect.left) / rect.width) * 100));
        render();
      };
      input.addEventListener('input', render);
      frame.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        this.dragging = true;
        frame.setPointerCapture(event.pointerId);
        fromPointer(event.clientX);
      });
      frame.addEventListener('pointermove', (event) => {
        if (this.dragging) fromPointer(event.clientX);
      });
      const end = () => {
        this.dragging = false;
      };
      frame.addEventListener('pointerup', end);
      frame.addEventListener('pointercancel', end);
      render();
    }
  }
  define('before-after', BeforeAfter);

  /* ------------------------------------------------------------------ */
  /* Count up                                                            */
  /* ------------------------------------------------------------------ */

  class CountUp extends HTMLElement {
    connectedCallback() {
      const raw = this.dataset.value || '0';
      const target = parseFloat(raw) || 0;
      const max = parseFloat(this.dataset.max) || 100;
      const decimals = (raw.split('.')[1] || '').length;
      const out = $('[data-count]', this) || this;
      const render = (value) => {
        out.textContent = value.toFixed(decimals);
        this.style.setProperty('--progress', Math.min(1, value / max));
      };
      if (reducedMotion || designMode || !('IntersectionObserver' in window)) {
        render(target);
        return;
      }
      render(0);
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          const started = performance.now();
          const duration = 1400;
          const tick = (now) => {
            const progress = Math.min(1, (now - started) / duration);
            render(target * (1 - Math.pow(1 - progress, 3)));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        },
        { threshold: 0.4 }
      );
      observer.observe(this);
    }
  }
  define('count-up', CountUp);

  /* ------------------------------------------------------------------ */
  /* Video that plays only while visible                                 */
  /* ------------------------------------------------------------------ */

  class InviewVideo extends HTMLElement {
    connectedCallback() {
      const video = $('video', this);
      if (!video) return;
      video.muted = true;
      video.playsInline = true;
      if (reducedMotion || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.5 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  define('inview-video', InviewVideo);

  /* ------------------------------------------------------------------ */
  /* Header                                                              */
  /* ------------------------------------------------------------------ */

  class DismissibleBar extends HTMLElement {
    connectedCallback() {
      const key = `snap-dismissed-${this.dataset.key || 'bar'}`;
      if (!designMode) {
        try {
          if (window.sessionStorage.getItem(key)) {
            this.hidden = true;
            return;
          }
        } catch (e) {
          /* storage blocked */
        }
      }
      const button = $('[data-dismiss]', this);
      if (!button) return;
      button.addEventListener('click', () => {
        this.hidden = true;
        try {
          window.sessionStorage.setItem(key, '1');
        } catch (e) {
          /* storage blocked */
        }
      });
    }
  }
  define('dismissible-bar', DismissibleBar);

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  define('sticky-header', StickyHeader);
})();
